import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, User, Building } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Footer from '../components/footer/index';
import { useDarkMode } from '../components/darkmode/DarkModeContext';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const ContactPage = () => {
  const { isDarkMode } = useDarkMode();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });

  // Custom icon for the marker
  const createCustomIcon = () => {
    return L.divIcon({
      html: `
        <div style="
          background-color: #1E9B4C;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        ">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"/>
          </svg>
        </div>
      `,
      className: 'custom-marker',
      iconSize: [40, 40],
      iconAnchor: [20, 40],
    });
  };

  const officeLocation = [5.548290, 95.323750]; // [latitude, longitude] for Banda Aceh

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // console.log('Form submitted:', formData);
    alert('Terima kasih! Pesan Anda telah terkirim. Tim kami akan segera menghubungi Anda.');
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  const contactInfo = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Alamat Kantor",
      details: ["Jl. Dr. Mr. Mohd. Hasan No. 33", "Kota Banda Aceh, 23245", "Provinsi Aceh, Indonesia"]
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Telepon",
      details: ["08777 888 2022", "+62 651 123 4567"]
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email",
      details: ["info@telnet.co.id", "support@telnet.co.id", "sales@telnet.co.id"]
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Jam Operasional",
      details: ["Senin - Jumat: 08:00 - 17:00", "Sabtu: 08:00 - 12:00", "Minggu: Tutup"]
    }
  ];

  const services = [
    "Telnet Home - Internet Rumahan",
    "Telnet Fiber - Solusi Bisnis",
    "Dedicated Internet",
    "Network Infrastructure",
    "Technical Support 24/7"
  ];

  return (
    <div
      className="min-h-screen bg-cover bg-center"
      style={{
        backgroundImage: `url('https://telnet.net.id/assets/bg.png')`,
        backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
        backgroundBlendMode: 'overlay'
      }}
    >
      {/* Breadcrumb */}
      <div className={`pt-20 py-3 px-6 ${isDarkMode ? 'bg-gray-800/50' : 'bg-[#DFE4E0]'} transition-colors`}>
        <div className="max-w-7xl mx-auto">
          <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
            <span>Home</span> / <span className="text-[#1E9B4C]">Contact</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className={`text-5xl lg:text-6xl font-bold mb-6 leading-tight ${isDarkMode ? 'text-green-50' : 'text-gray-900'}`}>
            Hubungi <span className="text-[#1E9B4C]">Kami</span>
          </h1>
          <p className={`text-lg mb-8 leading-relaxed max-w-3xl mx-auto ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Tim ahli kami siap membantu Anda dengan solusi internet terbaik. Jangan ragu untuk menghubungi kami kapan saja untuk konsultasi gratis tentang kebutuhan internet Anda.
          </p>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className={`rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300 border hover:scale-105
                  ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
              >
                <div className="text-[#1E9B4C] mb-4">{info.icon}</div>
                <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-green-50' : 'text-gray-900'}`}>
                  {info.title}
                </h3>
                <div className="space-y-2">
                  {info.details.map((detail, i) => (
                    <p key={i} className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-sm`}>
                      {detail}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div>
            <h2 className={`text-4xl font-bold mb-6 ${isDarkMode ? 'text-green-50' : 'text-gray-900'}`}>
              Kirim Pesan
            </h2>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-lg mb-8`}>
              Isi formulir di bawah ini dan tim kami akan segera menghubungi Anda dalam 24 jam.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Nama Lengkap *
                  </label>
                  <div className="relative">
                    <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className={`w-full pl-12 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#1E9B4C] focus:border-transparent transition-colors
                        ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      placeholder="Masukkan nama lengkap"
                    />
                  </div>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className={`w-full pl-12 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#1E9B4C] focus:border-transparent transition-colors
                        ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      placeholder="nama@email.com"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Perusahaan
                  </label>
                  <div className="relative">
                    <Building className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className={`w-full pl-12 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#1E9B4C] focus:border-transparent transition-colors
                        ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      placeholder="Nama perusahaan"
                    />
                  </div>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Nomor Telepon
                  </label>
                  <div className="relative">
                    <Phone className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full pl-12 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#1E9B4C] focus:border-transparent transition-colors
                        ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      placeholder="08xxx xxx xxxx"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Subjek *
                </label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#1E9B4C] focus:border-transparent transition-colors
                    ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="">Pilih subjek pesan</option>
                  <option value="telnet-home">Telnet Home - Internet Rumahan</option>
                  <option value="telnet-fiber">Telnet Fiber - Solusi Bisnis</option>
                  <option value="dedicated-internet">Dedicated Internet</option>
                  <option value="technical-support">Technical Support</option>
                  <option value="partnership">Kemitraan</option>
                  <option value="other">Lainnya</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Pesan *
                </label>
                <div className="relative">
                  <MessageCircle className={`absolute left-3 top-4 w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="6"
                    className={`w-full pl-12 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#1E9B4C] focus:border-transparent transition-colors resize-none
                      ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    placeholder="Ceritakan kebutuhan internet Anda..."
                  ></textarea>
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-[#1E9B4C] text-white py-4 px-6 rounded-lg font-semibold hover:bg-[#188A42] transition-colors flex items-center justify-center gap-2"
              >
                <Send className="w-5 h-5" />
                Kirim Pesan
              </button>
            </form>
          </div>

          {/* Services & Map */}
          <div className="space-y-8">
            {/* Services */}
            <div className={`rounded-xl p-8 shadow-md border
              ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <h3 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-green-50' : 'text-gray-900'}`}>
                Layanan Kami
              </h3>
              <ul className="space-y-3">
                {services.map((service, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#1E9B4C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{service}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Map */}
            <div className={`rounded-xl overflow-hidden shadow-md border
              ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className={`p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-green-50' : 'text-gray-900'}`}>
                  Lokasi Kantor
                </h3>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-sm mb-4`}>
                  Jl. Dr. Mr. Mohd. Hasan No. 33, Banda Aceh
                </p>
              </div>
              <div className="h-64 w-full">
                <MapContainer
                  center={officeLocation}
                  zoom={15}
                  style={{ height: '100%', width: '100%' }}
                  zoomControl={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={officeLocation} icon={createCustomIcon()}>
                    <Popup>
                      <div className="p-2">
                        <h3 className="font-bold text-[#1E9B4C] mb-1">Kantor Telnet</h3>
                        <p className="text-sm">Jl. Dr. Mr. Mohd. Hasan No. 33<br />Kota Banda Aceh, 23245</p>
                      </div>
                    </Popup>
                  </Marker>
                </MapContainer>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`${isDarkMode ? 'bg-gray-900' : ''} py-20 px-6`}
        style={!isDarkMode ? { backgroundColor: "#152E50" } : {}}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Butuh Bantuan Segera?
          </h2>
          <p className="text-gray-300 text-lg mb-8">
            Tim support teknis kami siap membantu Anda 24/7 untuk masalah urgent
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:087778882022"
              className="bg-[#1E9B4C] text-white px-8 py-4 rounded-md font-bold hover:bg-[#188A42] transition-colors inline-flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Telepon Sekarang
            </a>
            <a
              href="mailto:support@telnet.co.id"
              className="border-2 border-white text-white px-8 py-4 rounded-md font-bold hover:bg-white hover:text-[#152E50] transition-colors inline-flex items-center justify-center gap-2"
            >
              <Mail className="w-5 h-5" />
              Email Support
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;